/* eslint-disable no-template-curly-in-string */
// Field metadata registry for the OME application-config editor.
//
// The middleware canonical model is deliberately loose ({ [key]: any } bags),
// so the *dashboard* owns the knowledge of which control fits each field. This
// table is the single place that mapping lives: control kind + label + enum
// values + advanced flag + unit/help. The editor's renderer looks a field up by
// (node context, key); anything not found here falls back to a generic text
// input, so no field is ever dropped (full-schema editability is preserved).
//
// Enum values were taken from the real engine XMLs and the dashboard's strict
// context.ts unions. Engine scalars are strings, so number/checkbox fields still
// store strings ("30000" / "true") — the canonical model compares via String().

export type ControlType =
  | "text"
  | "number"
  | "checkbox"
  | "select"
  | "ref-select"
  | "template"
  | "url-list";

export interface FieldSpec {
  key: string;
  label: string;
  control: ControlType;
  options?: string[]; // for control === "select"
  refSource?: "video-encodes" | "audio-encodes"; // for control === "ref-select"
  advanced?: boolean;
  unit?: string;
  help?: string;
  placeholder?: string;
}

export type NodeContext =
  | "app-root"
  | "output-profile"
  | "encode-video"
  | "encode-audio"
  | "encode-image"
  | "bypassIfMatch-video"
  | "bypassIfMatch-audio"
  | "playlist"
  | "playlist-options"
  | "rendition"
  | "provider-multiplex"
  | "publisher-webrtc"
  | "publisher-llhls"
  | "publisher-hls"
  | "publisher-file"
  | "publishers-root";

const OPERATOR = ["eq", "lte", "gte"];

export const FIELD_SPECS: Record<NodeContext, FieldSpec[]> = {
  "app-root": [
    { key: "type", label: "Type", control: "select", options: ["live", "vod"] },
  ],
  "output-profile": [
    { key: "name", label: "Name", control: "text" },
    {
      key: "outputStreamName",
      label: "Output stream name",
      control: "template",
      placeholder: "${OriginStreamName}",
      help: "Usually ${OriginStreamName} — anything else breaks stream-key identity.",
    },
  ],
  "encode-video": [
    { key: "name", label: "Name", control: "text" },
    { key: "bypass", label: "Bypass (passthrough)", control: "checkbox" },
    { key: "codec", label: "Codec", control: "select", options: ["h264", "h265", "vp8"] },
    { key: "bitrate", label: "Bitrate", control: "number", unit: "bps" },
    { key: "width", label: "Width", control: "number", unit: "px" },
    { key: "height", label: "Height", control: "number", unit: "px" },
    { key: "framerate", label: "Framerate", control: "number", unit: "fps" },
    { key: "preset", label: "Preset", control: "select", options: ["slower", "slow", "medium", "fast", "faster"], advanced: true },
    { key: "profile", label: "Profile", control: "select", options: ["baseline", "main", "high"], advanced: true },
    { key: "keyFrameInterval", label: "Key-frame interval", control: "number", advanced: true },
    { key: "bFrames", label: "B-frames", control: "number", advanced: true },
    { key: "threadCount", label: "Thread count", control: "number", advanced: true },
  ],
  "encode-audio": [
    { key: "name", label: "Name", control: "text" },
    { key: "bypass", label: "Bypass (passthrough)", control: "checkbox" },
    { key: "codec", label: "Codec", control: "select", options: ["opus", "aac"] },
    { key: "bitrate", label: "Bitrate", control: "number", unit: "bps" },
    { key: "samplerate", label: "Sample rate", control: "number", unit: "Hz", advanced: true },
    { key: "channel", label: "Channels", control: "number", advanced: true },
  ],
  "encode-image": [
    { key: "name", label: "Name", control: "text" },
    { key: "codec", label: "Codec", control: "select", options: ["jpeg", "png", "webp"] },
    { key: "width", label: "Width", control: "number", unit: "px" },
    { key: "height", label: "Height", control: "number", unit: "px" },
    { key: "framerate", label: "Framerate", control: "number", unit: "fps" },
  ],
  "bypassIfMatch-video": [
    { key: "codec", label: "Codec", control: "select", options: ["eq"] },
    { key: "width", label: "Width", control: "select", options: OPERATOR },
    { key: "height", label: "Height", control: "select", options: OPERATOR },
    { key: "framerate", label: "Framerate", control: "select", options: OPERATOR },
    { key: "sar", label: "SAR", control: "select", options: ["eq"] },
  ],
  "bypassIfMatch-audio": [
    { key: "codec", label: "Codec", control: "select", options: ["eq"] },
    { key: "samplerate", label: "Sample rate", control: "select", options: OPERATOR },
    { key: "channel", label: "Channels", control: "select", options: OPERATOR },
  ],
  playlist: [
    { key: "name", label: "Name", control: "text" },
    { key: "fileName", label: "File name", control: "text", help: "The .m3u8 base name viewers request." },
  ],
  "playlist-options": [
    { key: "webRtcAutoAbr", label: "WebRTC auto-ABR", control: "checkbox" },
    { key: "enableTsPackaging", label: "Enable TS packaging", control: "checkbox" },
    { key: "hLSChunklistPathDepth", label: "HLS chunklist path depth", control: "number", advanced: true },
  ],
  rendition: [
    { key: "name", label: "Name", control: "text" },
    { key: "video", label: "Video", control: "ref-select", refSource: "video-encodes" },
    { key: "audio", label: "Audio", control: "ref-select", refSource: "audio-encodes" },
  ],
  "provider-multiplex": [
    { key: "muxFilesDir", label: "Mux files dir", control: "template", help: "Directory holding the .mux channel files." },
  ],
  "publisher-webrtc": [
    { key: "timeout", label: "Timeout", control: "number", unit: "ms" },
    { key: "rtx", label: "RTX", control: "checkbox" },
    { key: "ulpfec", label: "ULP FEC", control: "checkbox" },
    { key: "jitterBuffer", label: "Jitter buffer", control: "checkbox" },
    { key: "bandwidthEstimation", label: "Bandwidth estimation", control: "select", options: ["REMB", "TRANSPORT-CC"], advanced: true },
    { key: "createDefaultPlaylist", label: "Create default playlist", control: "checkbox", advanced: true },
  ],
  "publisher-llhls": [
    { key: "chunkDuration", label: "Chunk duration", control: "number", unit: "s" },
    { key: "segmentDuration", label: "Segment duration", control: "number", unit: "s" },
    { key: "segmentCount", label: "Segment count", control: "number" },
    { key: "originMode", label: "Origin mode", control: "checkbox", advanced: true },
    { key: "createDefaultPlaylist", label: "Create default playlist", control: "checkbox", advanced: true },
    { key: "crossDomains", label: "Cross domains", control: "url-list", advanced: true },
  ],
  "publisher-hls": [
    { key: "segmentCount", label: "Segment count", control: "number" },
    { key: "segmentDuration", label: "Segment duration", control: "number", unit: "s" },
    { key: "crossDomains", label: "Cross domains", control: "url-list", advanced: true },
  ],
  "publisher-file": [
    { key: "rootPath", label: "Root path", control: "template" },
    { key: "filePath", label: "File path", control: "template", help: "Supports ${VirtualHost}/${Application}/${Stream}/${StartTime:...} macros." },
    { key: "infoPath", label: "Info path", control: "template" },
  ],
  "publishers-root": [
    { key: "appWorkerCount", label: "App worker count", control: "number" },
    { key: "streamWorkerCount", label: "Stream worker count", control: "number" },
  ],
};

export interface PresenceSpec {
  key: string;
  label: string;
  nested?: NodeContext;
}

// Providers/publishers are objects keyed by protocol; presence of the key = the
// protocol is enabled (empty {} ≡ enabled-with-defaults). `nested` marks the
// ones that also carry editable child fields.
export const PROVIDERS: PresenceSpec[] = [
  { key: "srt", label: "SRT" },
  { key: "rtmp", label: "RTMP" },
  { key: "rtsp", label: "RTSP" },
  { key: "rtspPull", label: "RTSP Pull" },
  { key: "ovt", label: "OVT" },
  { key: "mpegts", label: "MPEG-TS" },
  { key: "webrtc", label: "WebRTC" },
  { key: "multiplex", label: "Multiplex", nested: "provider-multiplex" },
  { key: "scheduled", label: "Scheduled" },
  { key: "file", label: "File" },
];

export const PUBLISHERS: PresenceSpec[] = [
  { key: "webrtc", label: "WebRTC", nested: "publisher-webrtc" },
  { key: "llhls", label: "LLHLS", nested: "publisher-llhls" },
  { key: "hls", label: "HLS", nested: "publisher-hls" },
  { key: "file", label: "File", nested: "publisher-file" },
  { key: "ovt", label: "OVT" },
  { key: "rtmpPush", label: "RTMP Push" },
  { key: "srtPush", label: "SRT Push" },
  { key: "mpegtsPush", label: "MPEG-TS Push" },
  { key: "thumbnail", label: "Thumbnail" },
  { key: "dash", label: "DASH" },
];

// Scalar fields that live directly under <Publishers> (not protocol blocks).
export const PUBLISHERS_ROOT_KEYS = FIELD_SPECS["publishers-root"].map((spec) => spec.key);

// Minimal, safe skeletons for the typed "Add" buttons: real defaults where they
// are near-universal, and an EMPTY name so a fresh item never collides with an
// existing encode name (the silent-track-drop footgun).
export const ADD_SKELETONS = {
  outputProfile: (): any => ({ outputStreamName: "${OriginStreamName}" }),
  videoEncode: (): any => ({ codec: "h264", framerate: "30" }),
  audioEncode: (): any => ({ codec: "opus" }),
  imageEncode: (): any => ({ codec: "jpeg", framerate: "1" }),
  playlist: (): any => ({}),
  rendition: (): any => ({}),
};
